import { Component, DestroyRef, Renderer2, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { LayoutService } from '../services/layout.service';
import { MenuItem, MessageService } from 'primeng/api';
import { validate as isValidUUID } from 'uuid';
import { EMPTY, Subject, catchError, distinctUntilChanged, filter, map, switchMap } from 'rxjs';
import { MainComponent } from "../main/main.component";
import { CampaignApiService } from '../../core/services/campaign-api.service';
import { CampaignAction, PermissionService } from '../../core/services/permission.service';

const MENU_ACTIONS: CampaignAction[] = ['viewDashboard', 'viewReports', 'viewGroups', 'editCampaignDetails',
  'viewDetails', 'viewCampaignResult', 'manageCampaignUsers', 'manageLayouts', 'upload'];

@Component({
  selector: 'app-main-election',
  imports: [MainComponent],
  templateUrl: './main-election.component.html',
  styleUrl: './main-election.component.scss',
  providers: [MessageService]
})
export class MainElectionComponent {
    private permissions = inject(PermissionService);
    private destroyRef = inject(DestroyRef);

    model!: MenuItem[]
    campaignId!: string
    electionName!: string | null;
    daysRemaining!: number | null;
    /** The current campaign; switchMap drops answers for a campaign the user has already left. */
    private campaignIds = new Subject<string>();
    /**
     * The campaign on every navigation, even within one campaign, so a role change shows up in the menu once
     * PermissionService's cache expires (ROLE_CACHE_MS). switchMap drops a slower answer from an earlier navigation.
     */
    private menuChecks = new Subject<string>();

    constructor(
        public layoutService: LayoutService,
        public renderer: Renderer2,
        public router: Router,
        private campaignApi: CampaignApiService
    ) {
      this.campaignIds.pipe(
        switchMap(id => this.campaignApi.getCampaignById(id).pipe(catchError(err => {
          console.log(err);
          return EMPTY;
        }))),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(data => {
        this.electionName = data?.name;
        this.daysRemaining = this.calculateDaysRemaining(data.endDate);
      });
      this.menuChecks.pipe(
        switchMap(id => this.permissions.canAll$(MENU_ACTIONS, id).pipe(map(can => ({ id, can })))),
        // Rebuild only when the campaign or a permission changed: a new model array re-creates every menu item,
        // which would collapse an open submenu on each click.
        distinctUntilChanged((a, b) => a.id === b.id && JSON.stringify(a.can) === JSON.stringify(b.can)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(({ can }) => this.model = this.buildMenu(can));

      // Re-evaluated on every navigation, so moving between campaigns, or a role change, updates the menu (spec 004, US5).
      this.onUrl(router.url);
      router.events.pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(e => this.onUrl(e.urlAfterRedirects));
    }

  /** The campaign is the last UUID in the path (e.g. /election/edit/:id, /election/groups/edit/:groupId/:campaignId). */
  private onUrl(url: string) {
    const campaignId = url.split(/[?#]/)[0].split('/').reverse().find(segment => isValidUUID(segment));
    if (!campaignId) {
      return;
    }
    if (campaignId !== this.campaignId) {
      this.campaignId = campaignId;
      this.campaignIds.next(campaignId);
    }
    // Cheap: canAll$ reuses the cached role until it expires.
    this.menuChecks.next(campaignId);
  }

  calculateDaysRemaining(targetDate: Date) {

    if(targetDate) {
     const now = new Date();
     const difference = new Date(targetDate).getTime() - now.getTime();
     return Math.ceil(difference / (1000 * 60 * 60 * 24));
    }

    return null;
  }

  /** Every item comes from PermissionService, which mirrors what the server allows (contracts/frontend-permissions.md). */
  private buildMenu(can: Record<CampaignAction, boolean>): MenuItem[] {
    const election: MenuItem[] = [];
    if (can.viewDashboard) {
      election.push({ label: 'Dashboard', routerLink: ['/election/graphic/', this.campaignId] });
    }
    if (can.viewReports) {
      election.push({ label: 'Election Reporting', routerLink: ['/election/report', this.campaignId] });
    }
    if (can.viewGroups) {
      election.push({ label: 'Groups', routerLink: ['/election/groups/', this.campaignId] });
    }
    if (election.length > 0) {
      election.push({ separator: true });
    }
    if (can.editCampaignDetails) {
      election.push({ label: 'Detail', routerLink: ['/election/edit/', this.campaignId] });
    } else if (can.viewDetails) {
      election.push({ label: 'Detail', routerLink: ['/election/', this.campaignId] });
    }
    if (can.viewCampaignResult) {
      election.push({ label: 'Result', routerLink: ['/election/result/', this.campaignId] });
    }
    if (can.manageCampaignUsers) {
      election.push({ label: 'Users', routerLink: ['/election/associate-user/', this.campaignId] });
    }
    const files: MenuItem[] = [];
    if (can.manageLayouts) {
      files.push({ label: 'Layout', routerLink: ['/election/configuration/', this.campaignId] });
    }
    if (can.upload) {
      files.push({ label: 'Upload', routerLink: ['/upload/', this.campaignId] });
    }
    if (files.length > 0) {
      election.push({ label: 'Files', items: files });
    }

    const home: MenuItem = { label: 'Home', items: [{ label: 'Home', icon: 'pi pi-fw pi-home', routerLink: ['/'] }] };
    // Not a member at all: Home only, like before.
    return can.viewDashboard ? [home, { label: 'Election', items: election }] : [home];
  }

}
